import React, { useState } from 'react';
import { Portfolio, FamilyMember, CASUploadResponse } from '../types';
import { Upload, FileText, AlertCircle, CheckCircle, Loader, Info, UploadIcon } from 'lucide-react';
import apiService from '../services/api';

interface CASUploadProps {
  portfolio: Portfolio;
  setPortfolio: React.Dispatch<React.SetStateAction<Portfolio>>;
}

const CASUpload: React.FC<CASUploadProps> = ({ portfolio, setPortfolio }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [familyMemberId, setFamilyMemberId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadResult, setUploadResult] = useState<CASUploadResponse | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  const handleFileSelect = (file: File) => {
    if (file.type === 'application/pdf') {
      setSelectedFile(file);
      setUploadResult(null);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !familyMemberId || !password) {
      alert('Please fill all required fields');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('casFile', selectedFile);
      formData.append('familyMemberId', familyMemberId);
      formData.append('password', password);

      const result: CASUploadResponse = await apiService.uploadCasPdf(formData);
      setUploadResult(result);

      if (result.success) {
        // Refresh investments data
        const investments = await apiService.getInvestments();
        setPortfolio(prev => ({ ...prev, investments }));
        
        // Clear form
        setSelectedFile(null);
        setPassword('');
        
        // Reset file input
        const fileInput = document.getElementById('casFile') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        message: 'Failed to upload CAS file',
        errors: [error instanceof Error ? error.message : 'Unknown error']
      });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPassword('');
    setUploadResult(null);
    const fileInput = document.getElementById('casFile') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="p-6">
      <div className="flex items-start space-x-3 gap-6">
        <div className="flex items-center space-x-3">
          <UploadIcon className="h-6 w-6 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900">CAS Upload</h2>
        </div>
      </div>
      
      {/* Info Section */}
      <div className=" mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">About CAS Upload</h3>
            <p className="text-sm text-blue-700 mt-1">
              Upload your CDSL Consolidated Account Statement (CAS) PDF to automatically import your holdings. 
              The system will parse your investments and add them to the selected family member's portfolio.
            </p>
            <ul className="text-xs text-blue-600 mt-2 list-disc list-inside">
              <li>Only CDSL CAS files supported for now</li>
              <li>Password is typically your PAN in uppercase</li>
              <li>Only holdings data is imported (no personal information is stored)</li>
              <li>Existing investments with same ISIN will be updated</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Family Member Selection */}
          <div>
            <label htmlFor="familyMember" className="block text-sm font-medium text-gray-700 mb-2">
              Select Family Member *
            </label>
            <select
              id="familyMember"
              value={familyMemberId}
              onChange={(e) => setFamilyMemberId(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Choose a family member</option>
              {portfolio.familyMembers.map((member) => (
                <option key={member._id} value={member._id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CAS PDF File *
            </label>
            <div
              className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? 'border-primary-400 bg-primary-50'
                  : selectedFile
                  ? 'border-green-400 bg-green-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                id="casFile"
                type="file"
                accept=".pdf"
                onChange={handleFileInputChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              
              {selectedFile ? (
                <div className="flex items-center justify-center space-x-2">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">{selectedFile.name}</p>
                    <p className="text-xs text-green-600">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600">
                    Drag and drop your CAS PDF here, or{' '}
                    <span className="text-primary-600 font-medium">click to browse</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Maximum file size: 10MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              PDF Password *
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter PDF password (usually PAN in uppercase)"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              The password is typically your PAN number in uppercase letters
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={!selectedFile || !familyMemberId || !password || uploading}
              className="flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload & Parse CAS</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div className={`mt-6 rounded-lg p-4 ${
          uploadResult.success 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-start space-x-3">
            {uploadResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            )}
            <div className="flex-1">
              <h3 className={`text-sm font-medium ${
                uploadResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
              </h3>
              <p className={`text-sm mt-1 ${
                uploadResult.success ? 'text-green-700' : 'text-red-700'
              }`}>
                {uploadResult.message}
              </p>

              {/* Success Details */}
              {uploadResult.success && uploadResult.statement && (
                <div className="mt-3 text-sm text-green-700 space-y-3">
                  <div>
                    <p><strong>Depository:</strong> {uploadResult.statement.depository}</p>
                    <p><strong>Investor:</strong> {uploadResult.statement.investorName}</p>
                    <p><strong>Holdings:</strong> {uploadResult.statement.holdingsCount}</p>
                    <p><strong>Transactions:</strong> {uploadResult.statement.transactionsCount}</p>
                  </div>
                  
                  {/* Holdings Summary by Type */}
                  {uploadResult.holdingsByType && Object.keys(uploadResult.holdingsByType).length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-semibold mb-2">Holdings Identified by Investment Type</h4>
                      <div className="bg-gray-50 p-3 rounded">
                        <ul className="space-y-1">
                          {Object.entries(uploadResult.holdingsByType).map(([type, count]) => (
                            <li key={type} className="text-sm">
                              <strong>{type}:</strong> {count} {count === 1 ? 'holding' : 'holdings'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {uploadResult.investmentsCreated !== undefined && (
                    <p className="mt-2"><strong>New Investments Created:</strong> {uploadResult.investmentsCreated}</p>
                  )}
                  {uploadResult.investmentsUpdated !== undefined && (
                    <p className="mt-1"><strong>Investments Updated:</strong> {uploadResult.investmentsUpdated}</p>
                  )}
                </div>
              )}

              {/* Error Details */}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className={`text-sm font-medium ${
                    uploadResult.success ? 'text-yellow-800' : 'text-red-800'
                  }`}>
                    {uploadResult.success ? 'Warnings:' : 'Errors:'}
                  </p>
                  <ul className={`text-sm mt-1 list-disc list-inside ${
                    uploadResult.success ? 'text-yellow-700' : 'text-red-700'
                  }`}>
                    {uploadResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CASUpload;
